import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useLogoutUser } from '@/features/auth/hooks/mutations/logout.ts';
import { clearAuthStore, useAuthStore } from '@/services/store/use-auth-store.tsx';
import { useSessionQuery } from '@/features/auth/hooks/queries/session.ts';
import { UserRolesSchema } from '@/features/users/schemas/users.schema.ts';
import { useShallow } from 'zustand/react/shallow';
import { SessionContext, SessionContextType } from './use-session.ts';

const defaultSession: Omit<SessionContextType, 'logOut'> = {
  userId: '',
  firstName: '',
  lastName: '',
  email: '',
  status: 'Unknown',
  role: 'Unknown',
  isLoading: true,
  isAuthenticated: false,
  expiresAt: undefined,
};

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  const {
    expiresAt,
    isAuthenticated,
    role: storeRole,
    userId: storeUserId,
    setUserRole,
    setUserId,
  } = useAuthStore(
    useShallow((state) => ({
      expiresAt: state.refreshToken.expiresAt,
      isAuthenticated: state.isAuthenticated,
      role: state.role,
      userId: state.userId,
      setUserRole: state.setUserRole,
      setUserId: state.setUserId,
    }))
  );

  const role = storeRole || defaultSession.role;
  const userId = storeUserId || defaultSession.userId;

  const { data, isLoading } = useSessionQuery({ enabled: isAuthenticated });

  const { mutate: logout } = useLogoutUser();

  const logOut = useCallback(() => {
    logout();
    clearAuthStore();
    navigate('/');
  }, [navigate, logout]);

  useEffect(() => {
    if (data?.role) {
      try {
        const parsedRole = UserRolesSchema.parse(data.role);
        if (parsedRole !== role) {
          setUserRole(parsedRole);
        }
      } catch {
        if (role !== defaultSession.role) {
          setUserRole(defaultSession.role);
        }
      }
    }

    if (data?.userId && userId !== data?.userId) {
      setUserId(data.userId);
    }
  }, [data?.role, data?.userId, role, userId, setUserRole, setUserId]);

  const contextValue = useMemo(
    () => ({
      ...defaultSession,
      ...data,
      role,
      userId,
      isLoading,
      isAuthenticated,
      expiresAt,
      logOut,
    }),
    [data, role, userId, isLoading, isAuthenticated, expiresAt, logOut]
  );

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};
